import { StrictMode, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { DesignSystemProvider, NotistackAdapter } from '@actualtysoft/ui-kit';
import { Box } from '@mui/material';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Body from 'components/Layout/Body';
import Modal from 'components/Modal/Modal';
import MaterialThemeProvider from 'components/Theme/MaterialThemeProvider';
import { useAuthService } from 'core/auth.service';
import { SnackbarProvider } from 'notistack';
import { initSentry } from 'plugins/sentry';
import { useUserStore } from 'stores/user';

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = (): JSX.Element => {
  const { user, setUser } = useUserStore();
  const { initializeToken, getMe } = useAuthService();

  useEffect(() => {
    initializeToken();
  }, []);
  useEffect(() => {
    getMe().then((user) => setUser(user));
  }, [setUser]);

  if (!user.isAuthenticated) {
    return <Box data-testid="spinner" className="spinner" />;
  }

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <MaterialThemeProvider>
          <DesignSystemProvider>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: '100vh',
                backgroundColor: 'white',
              }}
            >
              <BrowserRouter
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <Body />
                <Modal />
                <SnackbarProvider
                  Components={{
                    info: NotistackAdapter,
                    success: NotistackAdapter,
                    error: NotistackAdapter,
                    warning: NotistackAdapter,
                  }}
                />
              </BrowserRouter>
            </Box>
          </DesignSystemProvider>
        </MaterialThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  );
};

export default Sentry.withProfiler(App);
